import { UsercentricsCMP } from "./types";

type WindowWithUsercentrics = Window &
  typeof globalThis & {
    UC_UI?: UsercentricsCMP;
  };

type UsercentricsEventListener = {
  /**
   * Perform an action when a Usercentrics specific event has occurred.
   * @param type Type of event to listen for.
   * @param callback Action to perform when the specified event has fired.
   */
  (type: "initialized", callback: () => void): void;
  /**
   * Perform an action when a Usercentrics specific event has occurred.
   * @param type Type of event to listen for.
   * @param callback Action to perform when the specified event has fired.
   * @param once Stop listening for further occurrences of the specified type when the event has fired once.
   */
  (type: "updated", callbac: () => void, once?: true): void;
};

type UsercentricsApi = {
  addEventListener: UsercentricsEventListener;
  /**
   * Checks if the user has given consent to a specific data processing service through a consent banner.
   * @param serviceName Check this user has given consent to this data processing service. Case sensitive.
   * @returns True if the user has given consent,
   *  false if the user has either actively declined or not made a decision yet.
   */
  getDataProcessingConsent: (serviceName: string) => boolean;

  /**
   * Programmatically give consent to one or more services. Non-existing ones will be ignored.
   * @param serviceNames Array of names of the services to consent to. Case sensitive.
   */
  acceptServices: (serviceNames: string[]) => void;

  /**
   * Determines if Usercentrics is enabled on the page.
   * Note: this isn't part of the native UsercentricsApi type from Usercentrics.
   */
  enabled: () => boolean;
};

const Usercentrics: UsercentricsApi = {
  addEventListener: (type, callback, once?) => {
    if (type === "initialized") {
      window.addEventListener("UC_UI_INITIALIZED", callback, { once: true });
      return;
    }

    // Window type does not contain a definition of "addEventListener" which supports custom events
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).addEventListener(
      "UC_UI_CMP_EVENT",
      (cmpEvent: CustomEvent) => {
        if (
          cmpEvent.detail.type === "SAVE" ||
          cmpEvent.detail.type === "ACCEPT_ALL" ||
          cmpEvent.detail.type === "DENY_ALL"
        ) {
          callback();
        }
      },
      { once }
    );
  },

  enabled: () => !!(document.getElementById("usercentrics-cmp") as HTMLScriptElement),

  getDataProcessingConsent: serviceName => {
    const usercentrics = (window as WindowWithUsercentrics).UC_UI as
      | UsercentricsCMP
      | undefined;

    if (!usercentrics || !usercentrics.isInitialized()) {
      console.warn("Usercentrics has not been initialized", usercentrics);
      return false;
    }

    if (usercentrics.areAllConsentsAccepted()) {
      return true;
    }

    const services = usercentrics.getServicesBaseInfo();

    const service = services.find(service => service.name === serviceName);

    return service?.consent.status ?? false;
  },

  acceptServices: (serviceNames: string[]) => {
    const usercentrics = (window as WindowWithUsercentrics).UC_UI as
      | UsercentricsCMP
      | undefined;

    if (!usercentrics || !usercentrics.isInitialized()) {
      console.warn("Usercentrics has not been initialized", usercentrics);
      return false;
    }

    const services = usercentrics.getServicesBaseInfo();

    const serviceIds: string[] = [];
    serviceNames.forEach(serviceName => {
      const service = services.find(service => service.name === serviceName);
      if (service) {
        serviceIds.push(service.id);
      }
    });

    if (serviceIds.length > 0) {
      usercentrics.acceptServices(serviceIds);
    }
  },
};

export default Usercentrics;
